import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, query, onSnapshot, where } from 'firebase/firestore';
import { getFirebase } from '@services/firebase';
import HomeProfileCard from '@components/HomeProfileCard';
import HomeFilter from '@components/HomeFilter';
import { FaTimes } from 'react-icons/fa';
import { multiKeywordMatch } from '../utils/searchUtils';
import { searchByImage } from '../services/imageSearch';

const { db } = getFirebase();

const SharedView = ({ searchQuery = '' }) => {
  const [searchParams] = useSearchParams();
  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [filters, setFilters] = useState(null);
  const [priceSort, setPriceSort] = useState('asc');
  const [expired, setExpired] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  const priceBoundsRef = useRef({ min: 0, max: 0 });
  const expiresAtRef = useRef(null);
  // Photo search from the top bar (same as Home).
  const [imageSearch, setImageSearch] = useState(null);
  const imageSearchRun = useRef(0);
  const profilesRef = useRef([]);
  useEffect(() => { profilesRef.current = profiles; }, [profiles]);

  useEffect(() => {
    const onImageSearch = async (e) => {
      const file = e.detail?.file;
      const run = ++imageSearchRun.current;
      if (!file) { setImageSearch(null); return; }
      setImageSearch({ status: 'working', message: 'Reading your photo…', ranking: null, tags: [] });
      try {
        const res = await searchByImage(file, profilesRef.current, (message) => {
          if (run === imageSearchRun.current) setImageSearch(s => (s ? { ...s, message } : s));
        });
        if (run !== imageSearchRun.current) return;
        setImageSearch({
          status: 'done',
          message: res.mode === 'exact'
            ? `${res.exactCount === 1 ? 'This cake' : `${res.exactCount} matching cakes`}${res.similarCount ? ' — you can also check out similar cakes below' : ''}`
            : res.isCake === false
              ? 'That photo does not look like a cake'
              : res.results.length
                ? 'Exact match not found, but you can check out these similar cakes'
                : 'Exact match not found, and no similar cakes either',
          ranking: new Map(res.results.map(r => [r.id, r.score])),
          tags: res.tags,
        });
      } catch (err) {
        if (run !== imageSearchRun.current) return;
        setImageSearch({ status: 'error', message: `Photo search failed: ${err.message}`, ranking: new Map(), tags: [] });
      }
    };
    window.addEventListener('pcake:image-search', onImageSearch);
    return () => window.removeEventListener('pcake:image-search', onImageSearch);
  }, []);

  const clearImageSearch = () => {
    imageSearchRun.current++;
    setImageSearch(null);
    window.dispatchEvent(new CustomEvent('pcake:image-search-cleared'));
  };

  const token = searchParams.get('t');

  // Decode token, set expiry ref
  useEffect(() => {
    if (!token) { setExpired(true); return; }
    try {
      const p = JSON.parse(atob(token));
      if (Date.now() > p.e) { setExpired(true); return; }
      expiresAtRef.current = p.e;
      setSecondsLeft(Math.ceil((p.e - Date.now()) / 1000));
      setFilters({
        priceRange: p.pr || [0, 9999],
        shapes:    p.s  || [],
        events:    p.ev || [],
        flavours:  p.fl || [],
        themes:    p.th || [],
        mostLiked: false,
        inOffer:   false,
      });
      setPriceSort(p.ps || 'asc');
    } catch {
      setExpired(true);
    }
  }, [token]);

  // Live countdown + expiry enforcement
  useEffect(() => {
    if (expiresAtRef.current === null) return;
    const interval = setInterval(() => {
      const remaining = Math.ceil((expiresAtRef.current - Date.now()) / 1000);
      if (remaining <= 0) {
        clearInterval(interval);
        window.history.replaceState(null, '', window.location.href);
        setExpired(true);
      } else {
        setSecondsLeft(remaining);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [expiresAtRef.current]);

  // Load visible profiles
  useEffect(() => {
    if (!db || filters === null) return;
    const q = query(collection(db, 'ctemplatespublished'), where('isflag', '==', true));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setProfiles(data);
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [filters]);

  // Init price bounds once profiles load
  useEffect(() => {
    if (!profiles.length || !filters) return;
    const prices = profiles
      .map(p => parseFloat(String(p.ccost || '').replace(/[^0-9.]/g, '')))
      .filter(n => !isNaN(n) && n > 0);
    if (!prices.length) return;
    const gMin = Math.floor(Math.min(...prices));
    const gMax = Math.ceil(Math.max(...prices));
    if (priceBoundsRef.current.min === gMin && priceBoundsRef.current.max === gMax) return;
    priceBoundsRef.current = { min: gMin, max: gMax };
    if (filters.priceRange[0] === 0 && filters.priceRange[1] === 9999) {
      setFilters(prev => ({ ...prev, priceRange: [gMin, gMax] }));
    }
  }, [profiles]);

  // Apply filters + sort
  useEffect(() => {
    if (!filters) return;
    const filtered = profiles.filter(profile => {
      if (searchQuery) {
        const fields = [
          profile.theme || '', profile.cflavour || '',
          ...(Array.isArray(profile.cevent) ? profile.cevent : []),
          ...(Array.isArray(profile.cshape) ? profile.cshape : []),
        ];
        if (!multiKeywordMatch(searchQuery, fields)) return false;
      }
      const { min: gMin, max: gMax } = priceBoundsRef.current;
      if (gMax > gMin) {
        const price = parseFloat(String(profile.ccost || '').replace(/[^0-9.]/g, ''));
        if (!isNaN(price) && price > 0) {
          if (price < filters.priceRange[0] || price > filters.priceRange[1]) return false;
        }
      }
      if (filters.shapes.length > 0) {
        if (!Array.isArray(profile.cshape) || !profile.cshape.some(s => filters.shapes.includes(s))) return false;
      }
      if (filters.events.length > 0) {
        if (!Array.isArray(profile.cevent) || !profile.cevent.some(e => filters.events.includes(e))) return false;
      }
      if (filters.flavours.length > 0) {
        if (!filters.flavours.includes(profile.cflavour)) return false;
      }
      if (filters.themes.length > 0) {
        if (!filters.themes.includes(profile.theme)) return false;
      }
      return true;
    });

    const { min: gMin, max: gMax } = priceBoundsRef.current;
    const priceRangeActive = gMax > gMin && (filters.priceRange[0] > gMin || filters.priceRange[1] < gMax);
    if (priceRangeActive) {
      filtered.sort((a, b) => {
        const pa = parseFloat(String(a.ccost || '').replace(/[^0-9.]/g, '')) || 0;
        const pb = parseFloat(String(b.ccost || '').replace(/[^0-9.]/g, '')) || 0;
        return priceSort === 'asc' ? pa - pb : pb - pa;
      });
    }
    if (imageSearch?.ranking) {
      const rank = imageSearch.ranking;
      setFilteredProfiles(filtered.filter(p => rank.has(p.id)).sort((a, b) => rank.get(b.id) - rank.get(a.id)));
      return;
    }
    setFilteredProfiles(filtered);
  }, [profiles, filters, priceSort, searchQuery, imageSearch]);

  if (expired) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '100vh', gap: '12px', padding: '20px',
        background: '#fff',
      }}>
        <p style={{ color: '#888', textAlign: 'center', maxWidth: '300px', margin: 0, fontSize: '15px' }}>
          This shared link is no longer valid.
        </p>
      </div>
    );
  }

  const left = secondsLeft || 0;
  const pad = (n) => String(n).padStart(2, '0');
  const days = Math.floor(left / 86400);
  const timeLeft = days > 0
    ? `${days}d ${Math.floor((left % 86400) / 3600)}h left`
    : `${pad(Math.floor(left / 3600))}:${pad(Math.floor((left % 3600) / 60))}:${pad(left % 60)}`;

  return (
    <div style={{ paddingBottom: '20px' }}>
      {/* Header banner */}
      <div style={{
        background: 'linear-gradient(135deg, #fa4848, #ff8c69)',
        color: '#fff', padding: '10px 16px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontSize: '13px',
      }}>
        <span>🎂 Shared Collection</span>
      </div>

      {/* Countdown timer */}
      {secondsLeft !== null && (
        <div style={{
          textAlign: 'center', padding: '6px 0', fontSize: '12px',
          color: secondsLeft <= 60 ? '#fa4848' : '#888',
          fontVariantNumeric: 'tabular-nums',
        }}>⏱ {timeLeft}</div>
      )}

      {/* Filter bar */}
      {filters && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 12px 0' }}>
          <HomeFilter
            profiles={profiles}
            userRole="User"
            filters={filters}
            onChange={setFilters}
            filteredCount={filteredProfiles.length}
            priceSort={priceSort}
            onPriceSortChange={setPriceSort}
            globalMin={priceBoundsRef.current.min}
            globalMax={priceBoundsRef.current.max}
          />
        </div>
      )}

      {imageSearch && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
          margin: '6px 10px', padding: '6px 10px', borderRadius: '8px', fontSize: '13px',
          background: '#fff0f0', color: '#7a2a2a',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            {imageSearch.status === 'working' && (
              <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(250,72,72,.3)', borderTopColor: '#fa4848', animation: 'social-spin .8s linear infinite', flexShrink: 0 }} />
            )}
            <span style={{ whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.35 }}>{imageSearch.message}</span>
          </span>
          <button onClick={clearImageSearch} title="Clear photo search" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: '2px' }}>
            <FaTimes />
          </button>
        </div>
      )}

      {loading && <p style={{ textAlign: 'center', padding: '20px' }}>Loading…</p>}

      {!loading && filteredProfiles.length > 0 && (
        <div className="card-container">
          {filteredProfiles.map(profile => (
            <div key={profile.id} className="profile-card-wrapper">
              <HomeProfileCard
                theme={profile.theme || ' '}
                flavour={profile.cflavour}
                filling={profile.cfilling || profile.cfilling1}
                cover={profile.cover}
                image={profile.cimage}
                showCustomIcon={false}
                showFavouriteIcon={false}
                showShareAction={false}
                showDeleteIcon={false}
                showVisibilityIcon={false}
                isInCustom={false}
                isInFavourites={false}
                isVisible={true}
                isGlobalProfile={true}
                onClick={undefined}
                userRole="User"
                profileId={profile.id}
              />
            </div>
          ))}
        </div>
      )}

      {!loading && filteredProfiles.length === 0 && (
        <p style={{ textAlign: 'center', color: '#888', marginTop: '40px' }}>No profiles match the current filters.</p>
      )}
    </div>
  );
};

export default SharedView;
